import { useState } from 'react';
import { useOperatorActions } from './lib/operatorActions';
import { useWallQueueItems } from './wallQueueData';
import { useWallTourCursors } from './WallTourOverlay';
import { newTourCardItems, WALL_TOUR_STOPS } from './wallTour';

/**
 * Wall risk-tour DESK clear strip (#272; PART XI Ruling 2.6).
 *
 * Clearing happens at the DESK, never the wall (read-only law): this compact
 * strip in the COP's Urgent Alerts queue panel mirrors the wall tour's SITREP
 * cards — one row per stop whose domain has something NEW since its
 * cleared-through cursor — and holds the Clear control. Clearing writes the
 * audited `clear_wall_tour_card` verb (payload `{stop, cleared_through}`):
 * an IMS-positive "the room reviewed fire risk at 14:45" record. The wall
 * mirrors the cleared state on its existing ≤30 s poll.
 *
 * The newness model is the WALL's exact model: the same national queue sweep
 * (`useWallQueueItems`) + the same `newTourCardItems` predicate + the same
 * read-time cursors — so the desk row and the wall card can never disagree
 * about what is outstanding. Quiet everywhere → renders NOTHING (no chrome;
 * a quiet tour is the success state).
 *
 * A clear updates an optimistic local cursor overlay so the row leaves
 * immediately; the server cursor catches up on the next poll (the verb sink
 * is fire-and-forget, flushed within ~1 s).
 */
export function WallTourDeskStrip(): JSX.Element | null {
  const items = useWallQueueItems();
  const serverCursors = useWallTourCursors();
  const { logAction } = useOperatorActions();
  const [localCursors, setLocalCursors] = useState<Record<string, string>>({});

  // Optimistic-over-server: the LATER instant wins per stop, so a stale server
  // poll can't resurrect a just-cleared row.
  const cursorFor = (stopId: string): string | null => {
    const server = serverCursors[stopId] ?? null;
    const local = localCursors[stopId] ?? null;
    if (server && local) return Date.parse(local) >= Date.parse(server) ? local : server;
    return local ?? server;
  };

  const rows = WALL_TOUR_STOPS.map((stop) => ({
    stop,
    fresh: newTourCardItems(items, stop, cursorFor(stop.id)),
  })).filter((r) => r.fresh.length > 0);

  if (rows.length === 0) return null;

  const clearStop = (stopId: string): void => {
    const clearedThrough = new Date().toISOString();
    logAction('clear_wall_tour_card', { stop: stopId, cleared_through: clearedThrough });
    setLocalCursors((prev) => ({ ...prev, [stopId]: clearedThrough }));
  };

  return (
    <div
      data-testid="wall-tour-desk-strip"
      className="border-b border-slate-800 bg-sky-500/5 px-3 py-1.5 space-y-1"
    >
      {rows.map(({ stop, fresh }) => (
        <div
          key={stop.id}
          data-testid={`wall-tour-desk-row-${stop.id}`}
          className="flex items-center gap-2 text-[11px] text-slate-300"
        >
          <span className="font-semibold uppercase tracking-wider text-sky-300">
            Wall tour · {stop.label}
          </span>
          <span className="truncate">
            {fresh.length} new since last review
          </span>
          <button
            type="button"
            data-testid={`wall-tour-clear-${stop.id}`}
            onClick={() => clearStop(stop.id)}
            className="ml-auto rounded bg-slate-700/60 px-1.5 py-0.5 text-slate-200 hover:bg-slate-600"
            title={`Mark the ${stop.label} tour domain reviewed through now (audited)`}
          >
            Clear
          </button>
        </div>
      ))}
    </div>
  );
}
